<template>
  <div class="status-matrix-root">
    <div class="header-main">
      <div class="hm-left">
        <div>
          <div class="bread-crumb"></div>
          <div class="header-main__title-wrap">
            <h1 class="header-main__title">Матрица переходов статусов</h1>
            <div class="header-main__review"></div>
          </div>
        </div>
      </div>
      <div class="hm-right matrix-header-controls">
        <label class="matrix-label">
          <span>Группа:</span>
          <select v-model="selectedGroup" class="matrix-select" @change="onGroupChange">
            <option value="">— Выберите группу —</option>
            <option v-for="g in userGroups" :key="g" :value="g">{{ g }}</option>
          </select>
        </label>
        <button
          v-if="selectedGroup && statuses.length"
          type="button"
          class="check-all-btn"
          @click="checkAll"
        >
          Отметить все
        </button>
        <button
          v-if="selectedGroup"
          class="save-matrix-btn"
          :disabled="saving"
          @click="saveMatrix"
        >
          {{ saving ? 'Сохранение…' : 'Сохранить' }}
        </button>
      </div>
    </div>

    <div class="status-matrix-section">

      <div v-if="loadingStatuses || loadingMatrix" class="matrix-loading">
        Загрузка…
      </div>

      <div v-else-if="selectedGroup && statuses.length" class="matrix-wrapper">
        <p class="matrix-hint">
          Из статуса слева можно переводить в статус сверху.
        </p>
        <div class="matrix-scroll">
          <table class="status-matrix-table">
            <thead>
              <tr>
                <th class="corner-cell"></th>
                <th
                  v-for="(col, colIdx) in sortedStatuses"
                  :key="col.code"
                  :class="['header-cell', { 'group-separator-col': isGroupStart(col, colIdx), 'header-cell--hover': hoveredColIdx === colIdx }]"
                  :title="col.name"
                  :style="{ backgroundColor: getStatusGroupColor(col.group || col.color) }"
                >
                  {{ col.name }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, rowIdx) in sortedStatuses"
                :key="row.code"
                :class="{ 'group-separator': isGroupStart(row, rowIdx) }"
              >
                <td
                  :class="['row-label', { 'row-label--hover': hoveredRowIdx === rowIdx }]"
                  :title="row.name"
                  :style="{ backgroundColor: getStatusGroupColor(row.group || row.color) }"
                >
                  {{ row.name }}
                </td>
                <td
                  v-for="(col, colIdx) in sortedStatuses"
                  :key="col.code"
                  :class="[
                    'cell',
                    {
                      'cell--row-hover': hoveredRowIdx === rowIdx,
                      'cell--col-hover': hoveredColIdx === colIdx,
                      'cell--cross': hoveredRowIdx === rowIdx && hoveredColIdx === colIdx,
                    },
                  ]"
                  @mouseenter="hoveredRowIdx = rowIdx; hoveredColIdx = colIdx"
                  @mouseleave="hoveredRowIdx = -1; hoveredColIdx = -1"
                >
                  <label class="checkbox-cell">
                    <input
                      type="checkbox"
                      :checked="isAllowed(row.code, col.code)"
                      :disabled="row.code === col.code"
                      @change="toggleTransition(row.code, col.code)"
                    />
                    <span class="checkmark"></span>
                  </label>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-else-if="selectedGroup && !loadingStatuses" class="matrix-empty">
        Нет данных о статусах или группа не выбрана.
      </div>
    </div>

    <!-- Правила полей по статусам для выбранной группы -->
    <div v-if="selectedGroup && statuses.length" class="field-rules-section">
      <h2 class="field-rules-title">Правила полей по статусам</h2>
      <div v-if="loadingFieldRules" class="matrix-loading">Загрузка правил…</div>
      <template v-else>
        <div
          v-for="(rule, ruleIdx) in fieldRules"
          :key="ruleIdx"
          class="field-rule-block"
        >
          <div class="field-rule-block-header">
            <span class="field-rule-block-num">Блок {{ ruleIdx + 1 }}</span>
            <button
              type="button"
              class="field-rule-remove-btn"
              @click="removeFieldRuleBlock(ruleIdx)"
              title="Удалить блок"
            >
              Удалить
            </button>
          </div>
          <div class="field-rule-row">
            <label class="field-rule-label">Статусы (при которых действует правило)</label>
            <Treeselect
              v-model="rule.statuses"
              :options="statusTreeOptions"
              :multiple="true"
              :flat="false"
              value-consists-of="LEAF_PRIORITY"
              placeholder="Выберите статусы (можно группой)..."
              class="field-rule-treeselect"
            />
          </div>
          <div class="field-rule-row">
            <label class="field-rule-label">Разрешённые поля</label>
            <div class="field-rule-treeselect-row">
              <Treeselect
                v-model="rule.allowedFields"
                :options="orderEditFieldOptions"
                :multiple="true"
                placeholder="Выберите разрешённые поля..."
                class="field-rule-treeselect"
              />
              <div class="field-treeselect-actions">
                <button
                  type="button"
                  class="field-fields-action-btn"
                  @click="setAllOrderEditFields(rule, 'allowedFields')"
                >
                  Выбрать все
                </button>
                <button
                  type="button"
                  class="field-fields-action-btn field-fields-action-btn--muted"
                  @click="clearOrderEditFields(rule, 'allowedFields')"
                >
                  Снять выделение
                </button>
              </div>
            </div>
          </div>
          <div class="field-rule-row">
            <label class="field-rule-label">Запрещённые поля</label>
            <div class="field-rule-treeselect-row">
              <Treeselect
                v-model="rule.forbiddenFields"
                :options="orderEditFieldOptions"
                :multiple="true"
                placeholder="Выберите запрещённые поля..."
                class="field-rule-treeselect"
              />
              <div class="field-treeselect-actions">
                <button
                  type="button"
                  class="field-fields-action-btn"
                  @click="setAllOrderEditFields(rule, 'forbiddenFields')"
                >
                  Выбрать все
                </button>
                <button
                  type="button"
                  class="field-fields-action-btn field-fields-action-btn--muted"
                  @click="clearOrderEditFields(rule, 'forbiddenFields')"
                >
                  Снять выделение
                </button>
              </div>
            </div>
          </div>
        </div>
        <div class="field-rules-actions">
          <button type="button" class="add-block-btn" @click="addFieldRuleBlock">
            + Добавить блок
          </button>
          <button
            type="button"
            class="save-matrix-btn"
            :disabled="savingFieldRules"
            @click="saveFieldRules"
          >
            {{ savingFieldRules ? 'Сохранение…' : 'Сохранить правила полей' }}
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { ORDERS_API_BASE } from '@/config/api';
import Treeselect from '@riophae/vue-treeselect';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';
import { ORDER_EDIT_FIELDS } from '@/constants/orderEditFields.js';

export default {
  name: 'StatusMatrix',
  components: { Treeselect },
  data() {
    return {
      userGroups: [],
      selectedGroup: '',
      statuses: [],
      matrix: {},
      loadingStatuses: false,
      loadingMatrix: false,
      saving: false,
      hoveredRowIdx: -1,
      hoveredColIdx: -1,
      fieldRules: [],
      loadingFieldRules: false,
      savingFieldRules: false,
    };
  },
  computed: {
    statusTreeOptions() {
      const list = this.sortedStatuses;
      if (!list || !list.length) return [];
      const groupOrder = ['new', 'approval', 'assembling', 'delivery', 'complete', 'cancel', 'dflkdslskl', 'brak12', 'sto12f'];
      const byGroup = {};
      list.forEach((s) => {
        const g = s.group || s.color || 'other';
        if (!byGroup[g]) byGroup[g] = [];
        byGroup[g].push({ id: s.code, label: s.name || s.code });
      });
      const groupLabels = {
        new: 'Новые',
        approval: 'Согласование',
        assembling: 'Сборка',
        delivery: 'Доставка',
        complete: 'Завершённые',
        cancel: 'Отмены',
        dflkdslskl: 'Другое',
        brak12: 'Брак',
        sto12f: 'СТО',
        other: 'Прочие',
      };
      const result = [];
      groupOrder.forEach((g) => {
        if (!byGroup[g]) return;
        result.push({
          id: `group-${g}`,
          label: groupLabels[g] || g,
          children: byGroup[g],
        });
      });
      Object.keys(byGroup).filter((g) => !groupOrder.includes(g)).forEach((g) => {
        result.push({
          id: `group-${g}`,
          label: g,
          children: byGroup[g],
        });
      });
      return result;
    },
    orderEditFieldOptions() {
      return ORDER_EDIT_FIELDS.map((f) => ({ id: f.id, label: f.label }));
    },
    sortedStatuses() {
      const raw = this.statuses;
      if (!raw || !raw.length) return [];

      const groupOrder = ['new', 'approval', 'assembling', 'delivery', 'complete', 'cancel', 'dflkdslskl', 'brak12', 'sto12f'];

      const grouped = raw.reduce((acc, s) => {
        const g = s.group || s.color || 'other';
        if (!acc[g]) acc[g] = [];
        acc[g].push(s);
        return acc;
      }, {});

      const getNumber = (name) => {
        if (!name) return 999999;
        const m = String(name).match(/^(\d+)\./);
        return m ? parseInt(m[1], 10) : 999999;
      };

      const result = [];
      const seen = new Set(groupOrder);
      groupOrder.forEach((g) => {
        if (!grouped[g]) return;
        const list = [...grouped[g]].sort((a, b) => {
          const na = getNumber(a.name);
          const nb = getNumber(b.name);
          if (na !== 999999 && nb !== 999999) return na - nb;
          if (na !== 999999) return -1;
          if (nb !== 999999) return 1;
          return (a.name || '').localeCompare(b.name || '');
        });
        result.push(...list);
      });

      Object.keys(grouped).filter((g) => !seen.has(g)).forEach((g) => {
        const list = grouped[g].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        result.push(...list);
      });

      return result;
    },
  },
  mounted() {
    this.loadUserGroups();
  },
  methods: {
    getStatusGroupColor(colorName) {
      const colors = {
        new: '#fea530',
        approval: '#ffe0b2',
        delivery: '#bbdefb',
        complete: '#b2dfdb',
        cancel: '#ffcdd2',
        assembling: '#d1c4e9',
        dflkdslskl: '#b0bec5',
        brak12: '#d1c4e9',
        sto12f: 'rgb(119 128 219)',
      };
      return colors[colorName] || '#e8e8e8';
    },
    truncate(str, maxLen) {
      if (!str || str.length <= maxLen) return str;
      return str.slice(0, maxLen - 2) + '…';
    },
    async loadUserGroups() {
      try {
        const res = await fetch('https://manager.1akb.by/user-groups');
        this.userGroups = await res.json();
      } catch (e) {
        console.error('Ошибка загрузки групп:', e);
        this.userGroups = [];
      }
    },
    async loadStatuses() {
      this.loadingStatuses = true;
      try {
        const res = await fetch(ORDERS_API_BASE + '/statuses.php');
        const data = await res.json();
        this.statuses = (data.statuses || []).filter((s) => s.code);
        return Promise.resolve();
      } catch (e) {
        console.error('Ошибка загрузки статусов:', e);
        this.statuses = [];
        return Promise.reject(e);
      } finally {
        this.loadingStatuses = false;
      }
    },
    async loadMatrix() {
      if (!this.selectedGroup) return;
      this.loadingMatrix = true;
      try {
        const res = await fetch(
          `https://manager.1akb.by/status-transitions-matrix?group=${encodeURIComponent(this.selectedGroup)}`
        );
        const data = await res.json();
        this.matrix = data.matrix || {};
      } catch (e) {
        console.error('Ошибка загрузки матрицы:', e);
        this.matrix = {};
      } finally {
        this.loadingMatrix = false;
      }
    },
    onGroupChange() {
      this.matrix = {};
      this.fieldRules = [];
      if (!this.selectedGroup) return;
      if (!this.statuses.length) {
        this.loadStatuses().then(() => {
          this.loadMatrix();
          this.loadFieldRules();
        });
      } else {
        this.loadMatrix();
        this.loadFieldRules();
      }
    },
    addFieldRuleBlock() {
      this.fieldRules.push({
        statuses: [],
        allowedFields: [],
        forbiddenFields: [],
      });
    },
    removeFieldRuleBlock(index) {
      this.fieldRules.splice(index, 1);
    },
    setAllOrderEditFields(rule, fieldKey) {
      const ids = this.orderEditFieldOptions.map((o) => o.id);
      this.$set(rule, fieldKey, ids.slice());
    },
    clearOrderEditFields(rule, fieldKey) {
      this.$set(rule, fieldKey, []);
    },
    async loadFieldRules() {
      if (!this.selectedGroup) return;
      this.loadingFieldRules = true;
      try {
        const res = await fetch(
          `https://manager.1akb.by/status-field-rules?group=${encodeURIComponent(this.selectedGroup)}`
        );
        const data = await res.json();
        const list = data.rules || [];
        this.fieldRules = list.map((r) => ({
          statuses: Array.isArray(r.statuses) ? r.statuses : [],
          allowedFields: Array.isArray(r.allowedFields) ? r.allowedFields : [],
          forbiddenFields: Array.isArray(r.forbiddenFields) ? r.forbiddenFields : [],
        }));
      } catch (e) {
        console.error('Ошибка загрузки правил полей:', e);
        this.fieldRules = [];
      } finally {
        this.loadingFieldRules = false;
      }
    },
    async saveFieldRules() {
      if (!this.selectedGroup) return;
      this.savingFieldRules = true;
      try {
        const payload = this.fieldRules.map((r) => ({
          statuses: r.statuses || [],
          allowedFields: r.allowedFields || [],
          forbiddenFields: r.forbiddenFields || [],
        }));
        const res = await fetch('https://manager.1akb.by/status-field-rules', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ group: this.selectedGroup, rules: payload }),
        });
        const data = await res.json();
        if (data.success) {
          if (this.$bvToast && this.$bvToast.toast) {
            this.$bvToast.toast('Правила полей сохранены', {
              title: 'Успешно',
              variant: 'success',
              solid: true,
            });
          } else {
            alert('Правила полей сохранены.');
          }
        } else {
          alert('Ошибка: ' + (data.error || 'Не удалось сохранить'));
        }
      } catch (e) {
        console.error('Ошибка сохранения правил:', e);
        alert('Ошибка при сохранении');
      } finally {
        this.savingFieldRules = false;
      }
    },
    isAllowed(fromCode, toCode) {
      if (fromCode === toCode) return true;
      const row = this.matrix[fromCode];
      if (!row || typeof row !== 'object') return false;
      return !!row[toCode];
    },
    isGroupStart(row, index) {
      if (index === 0) return false;
      const prev = this.sortedStatuses[index - 1];
      return prev && (prev.group || prev.color) !== (row.group || row.color);
    },
    toggleTransition(fromCode, toCode) {
      if (fromCode === toCode) return;
      if (!this.matrix[fromCode]) {
        this.$set(this.matrix, fromCode, {});
      }
      const row = this.matrix[fromCode];
      const current = !!row[toCode];
      this.$set(row, toCode, !current);
    },
    checkAll() {
      const list = this.sortedStatuses;
      if (!list || !list.length) return;
      list.forEach((row) => {
        const fromCode = row.code;
        if (!fromCode) return;
        if (!this.matrix[fromCode]) {
          this.$set(this.matrix, fromCode, {});
        }
        list.forEach((col) => {
          const toCode = col.code;
          if (fromCode === toCode || !toCode) return;
          this.$set(this.matrix[fromCode], toCode, true);
        });
      });
    },
    async saveMatrix() {
      if (!this.selectedGroup) return;
      this.saving = true;
      try {
        const res = await fetch('https://manager.1akb.by/status-transitions-matrix', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            group: this.selectedGroup,
            matrix: this.matrix,
          }),
        });
        const data = await res.json();
        if (data.success) {
          if (this.$bvToast && this.$bvToast.toast) {
            this.$bvToast.toast('Матрица сохранена', {
              title: 'Успешно',
              variant: 'success',
              solid: true,
            });
          } else {
            alert('Матрица сохранена.');
          }
        } else {
          alert('Ошибка: ' + (data.error || 'Не удалось сохранить'));
        }
      } catch (e) {
        console.error('Ошибка сохранения:', e);
        alert('Ошибка при сохранении');
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.status-matrix-root {
  max-width: 100vw;
  overflow-x: hidden;
}

.status-matrix-section {
  margin-top: 24px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.08);
  padding: 24px;
  max-width: 100%;
}

.status-matrix-section .matrix-hint {
  margin-top: 0;
}

.matrix-header-controls {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.matrix-label {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
  color: #333;
}

.matrix-select {
  padding: 10px 14px;
  border-radius: 6px;
  border: 1px solid #d3d3d3;
  background: #fafafa;
  font-size: 14px;
  min-width: 200px;
  cursor: pointer;
}

.matrix-select:focus {
  outline: none;
  border-color: #2979ff;
}

.save-matrix-btn {
  padding: 10px 20px;
  background: #2979ff;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.save-matrix-btn:hover:not(:disabled) {
  background: #1852a6;
}

.save-matrix-btn:disabled {
  background: #90a4ae;
  cursor: not-allowed;
}

.check-all-btn {
  padding: 10px 16px;
  background: #fff;
  color: #2979ff;
  border: 1px solid #2979ff;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
}

.check-all-btn:hover {
  background: #e3f2fd;
  color: #1852a6;
}

.matrix-loading,
.matrix-empty {
  padding: 40px;
  text-align: center;
  color: #666;
  font-size: 15px;
}

.matrix-hint {
  margin-bottom: 16px;
  color: #555;
  font-size: 13px;
}

.matrix-wrapper {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
}

.matrix-scroll {
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  overflow-y: auto;
  max-height: 70vh;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}

.status-matrix-table {
  border-collapse: separate;
  border-spacing: 0;
  font-size: 10px;
  width: max-content;
  min-width: 100%;
}

.status-matrix-table th,
.status-matrix-table td {
  border: 1px solid #e0e0e0;
  padding: 6px 8px;
  text-align: center;
}

.corner-cell {
  background: #f5f5f5;
  min-width: 120px;
  width: 120px;
  position: sticky;
  top: 0;
  left: 0;
  z-index: 3;
  box-shadow: 2px 2px 0 0 #e0e0e0;
}

.header-cell {
  font-weight: 600;
  color: #333;
  white-space: normal;
  word-break: break-word;
  min-width: 60px;
  max-width: 90px;
  line-height: 1.2;
  vertical-align: middle;
  padding: 4px 6px;
  position: sticky;
  top: 0;
  z-index: 2;
  box-shadow: 0 1px 0 0 #e0e0e0;
  transition: box-shadow 0.15s ease;
}

.header-cell--hover {
  box-shadow: 0 2px 0 0 #2979ff, inset 0 0 0 2px rgba(41, 121, 255, 0.3);
  z-index: 4;
}

.row-label {
  text-align: left;
  font-weight: 500;
  white-space: normal;
  word-break: break-word;
  min-width: 120px;
  width: 120px;
  line-height: 1.2;
  vertical-align: middle;
  padding: 4px 6px;
  position: sticky;
  left: 0;
  z-index: 2;
  box-shadow: 1px 0 0 0 #e0e0e0;
  transition: box-shadow 0.15s ease, background-color 0.15s ease;
}

.row-label--hover {
  box-shadow: 2px 0 0 0 #2979ff, inset 0 0 0 2px rgba(41, 121, 255, 0.3);
  z-index: 4;
}

.cell {
  background: #fff;
  transition: box-shadow 0.15s ease, background-color 0.15s ease;
}

.cell--row-hover {
  background: #e3f2fd;
  box-shadow: inset 1px 0 0 0 rgba(41, 121, 255, 0.4);
}

.cell--col-hover {
  background: #e3f2fd;
  box-shadow: inset 0 1px 0 0 rgba(41, 121, 255, 0.4);
}

.cell--cross {
  background: #bbdefb;
  box-shadow: inset 0 0 0 2px #2979ff;
}

.status-matrix-table tr.group-separator td {
  border-top: 2px solid #999;
}

.status-matrix-table th.header-cell.group-separator-col {
  border-left: 2px solid #999;
}

.checkbox-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin: 0;
}

.checkbox-cell input {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.checkbox-cell input:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.header-main {
  display: table;
  empty-cells: hide;
  padding: 32px 0 24px;
}

.hm-left {
  color: #1e2248;
  float: left;
  width: 75%;
}

.hm-right {
  float: right;
  vertical-align: top;
}

/* Правила полей по статусам */
.field-rules-section {
  margin-top: 32px;
  padding: 24px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.08);
}

.field-rules-title {
  margin: 0 0 8px;
  font-size: 20px;
  color: #1e2248;
}

.field-rule-block {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  background: #fafafa;
}

.field-rule-block-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.field-rule-block-num {
  font-weight: 600;
  color: #333;
}

.field-rule-remove-btn {
  padding: 6px 12px;
  background: #fff;
  color: #c62828;
  border: 1px solid #ef9a9a;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
}

.field-rule-remove-btn:hover {
  background: #ffebee;
}

.field-rule-row {
  margin-bottom: 14px;
}

.field-rule-row:last-child {
  margin-bottom: 0;
}

.field-rule-label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #555;
  margin-bottom: 6px;
}

.field-rule-treeselect-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
}

.field-rule-treeselect-row .field-rule-treeselect {
  flex: 1;
  min-width: 220px;
  max-width: 100%;
}

.field-treeselect-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  flex-shrink: 0;
  padding-top: 2px;
}

.field-fields-action-btn {
  padding: 8px 12px;
  background: #fff;
  color: #2979ff;
  border: 1px solid #2979ff;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.field-fields-action-btn:hover {
  background: #e3f2fd;
}

.field-fields-action-btn--muted {
  color: #546e7a;
  border-color: #b0bec5;
}

.field-fields-action-btn--muted:hover {
  background: #eceff1;
  color: #37474f;
}

.field-rules-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 16px;
}

.add-block-btn {
  padding: 10px 18px;
  background: #fff;
  color: #2979ff;
  border: 1px solid #2979ff;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
}

.add-block-btn:hover {
  background: #e3f2fd;
}
</style>
